import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { useSubchapterNavigationContext } from "@/hooks/useSubchapterNavigationContext";
import {
  buildOutroContentEntryQueryString,
  resolveLastSubOutroChapterId,
  stripCourseNavQueryString,
} from "@/lib/chapterNavigation";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import type { CourseTreeNode } from "@/types/generated";

interface SubchapterNavigationProps {
  /** Override when the route selects a chapter but nav should target an embedded subchapter. */
  subchapterId?: string;
}

/**
 * Shared chrome for Zurück / Weiter.
 * Keep the xl icon-side edge padding (`px-6`) on both ends so text↔edge
 * matches icon↔edge.
 */
const NAV_BUTTON_CLASS = "px-6 has-[>svg]:px-6";
const NAV_CHEVRON_CLASS = "size-5 shrink-0";

interface PrevNavControlProps {
  basePath: string;
  contentSearch: string;
  isFirstInChapter: boolean;
  prevSubchapter: CourseTreeNode | null;
}

/**
 * Left-side control: Zurück, or an empty spacer for layout.
 */
const PrevNavControl = ({
  basePath,
  contentSearch,
  isFirstInChapter,
  prevSubchapter,
}: PrevNavControlProps) => {
  const showBackButton = !isFirstInChapter && prevSubchapter != null;

  if (showBackButton) {
    return (
      <Button asChild variant="default" size="xl" className={NAV_BUTTON_CLASS}>
        <Link to={`${basePath}/${prevSubchapter.id}${contentSearch}`}>
          <ChevronLeft className={NAV_CHEVRON_CLASS} />
          Zurück
        </Link>
      </Button>
    );
  }

  return <div />;
};

interface NextNavControlProps {
  basePath: string;
  contentSearch: string;
  nextSubchapter: CourseTreeNode | null;
}

/**
 * Right-side control: Weiter to the next lesson (or stacked outro content entry
 * when that lesson is the chapter’s last subchapter with outro), or spacer.
 */
const NextNavControl = ({
  basePath,
  contentSearch,
  nextSubchapter,
}: NextNavControlProps) => {
  const nodeIndex = useCourseDetailStore((state) => state.nodeIndex);

  if (!nextSubchapter) {
    return <div />;
  }

  const outroChapterId = resolveLastSubOutroChapterId(
    nextSubchapter.id,
    nodeIndex
  );
  const targetPath = outroChapterId
    ? `${basePath}/${outroChapterId}${buildOutroContentEntryQueryString(contentSearch)}`
    : `${basePath}/${nextSubchapter.id}${contentSearch}`;

  return (
    <Button asChild variant="default" size="xl" className={NAV_BUTTON_CLASS}>
      <Link to={targetPath}>
        Weiter
        <ChevronRight className={NAV_CHEVRON_CLASS} />
      </Link>
    </Button>
  );
};

/**
 * Prev/next controls between subchapters in a chapter. Last-subchapter →
 * Abschluss uses the stacked outro route instead of a finish button.
 */
export const SubchapterNavigation = ({
  subchapterId,
}: SubchapterNavigationProps) => {
  const basePath = useCourseBasePath();
  const location = useLocation();
  const contentSearch = stripCourseNavQueryString(location.search);

  const { isFirstInChapter, prevSubchapter, nextSubchapter } =
    useSubchapterNavigationContext(subchapterId);

  const showPrevButton = !isFirstInChapter && prevSubchapter != null;
  const showNextButton = nextSubchapter != null;

  if (!showPrevButton && !showNextButton) return null;

  return (
    <nav className="flex items-stretch justify-between gap-4 mt-12">
      <PrevNavControl
        basePath={basePath}
        contentSearch={contentSearch}
        isFirstInChapter={isFirstInChapter}
        prevSubchapter={prevSubchapter}
      />
      <NextNavControl
        basePath={basePath}
        contentSearch={contentSearch}
        nextSubchapter={nextSubchapter}
      />
    </nav>
  );
};
